import { z } from 'zod';
import {
    CloverFrontendConfigEditingOutcome,
    type CloverFrontendConfigEditingNormalizedResult,
    type CloverFrontendConfigEditingRequestDetails,
    type CloverFrontendConfigEditingResponseDetails,
    type CloverFrontendConfigEditorValues,
} from '../../../types/ipg/clover/clover-frontend-config-editing.types';
import { CloverGatewayId } from '../../../types/ipg/clover/clover-system-constants';

export const CloverFrontendConfigEditorValuesSchema: z.ZodObject<{
    enabled: z.ZodBoolean;
    merchantId: z.ZodString;
    ecommerceApiBaseUrl: z.ZodString;
    ecommerceTokenizationUrl: z.ZodString;
    restPayDisplayApiBaseUrl: z.ZodString;
}> = z.object({
    enabled: z.boolean(),
    merchantId: z.string().min(1),
    ecommerceApiBaseUrl: z.string().url(),
    ecommerceTokenizationUrl: z.string().url(),
    restPayDisplayApiBaseUrl: z.string().url(),
}) satisfies z.ZodType<CloverFrontendConfigEditorValues>;

export const CloverFrontendConfigEditingRequestDetailsSchema: z.ZodObject<{
    gatewayId: z.ZodLiteral<typeof CloverGatewayId>;
    editorValues: typeof CloverFrontendConfigEditorValuesSchema;
}> = z.object({
    gatewayId: z.literal(CloverGatewayId),
    editorValues: CloverFrontendConfigEditorValuesSchema,
}) satisfies z.ZodType<CloverFrontendConfigEditingRequestDetails>;

export const CloverFrontendConfigEditingNormalizedResultSchema: z.ZodObject<{
    gatewayId: z.ZodLiteral<typeof CloverGatewayId>;
    enabled: z.ZodBoolean;
    merchantId: z.ZodString;
    ecommerceApiBaseUrl: z.ZodString;
    ecommerceTokenizationUrl: z.ZodString;
    restPayDisplayApiBaseUrl: z.ZodString;
}> = z.object({
    gatewayId: z.literal(CloverGatewayId),
    enabled: z.boolean(),
    merchantId: z.string().min(1),
    ecommerceApiBaseUrl: z.string().url(),
    ecommerceTokenizationUrl: z.string().url(),
    restPayDisplayApiBaseUrl: z.string().url(),
}) satisfies z.ZodType<CloverFrontendConfigEditingNormalizedResult>;

export const CloverFrontendConfigEditingAppliedResponseDetailsSchema: z.ZodObject<{
    outcome: z.ZodLiteral<typeof CloverFrontendConfigEditingOutcome.APPLIED>;
    normalizedResult: typeof CloverFrontendConfigEditingNormalizedResultSchema;
}> = z.object({
    outcome: z.literal(CloverFrontendConfigEditingOutcome.APPLIED),
    normalizedResult: CloverFrontendConfigEditingNormalizedResultSchema,
});

export const CloverFrontendConfigEditingInvalidConfigurationResponseDetailsSchema: z.ZodObject<{
    outcome: z.ZodLiteral<
        typeof CloverFrontendConfigEditingOutcome.INVALID_CONFIGURATION
    >;
    message: z.ZodString;
    body: z.ZodUnknown;
}> = z.object({
    outcome: z.literal(CloverFrontendConfigEditingOutcome.INVALID_CONFIGURATION),
    message: z.string().min(1),
    body: z.unknown(),
});

export const CloverFrontendConfigEditingResponseDetailsSchema: z.ZodType<CloverFrontendConfigEditingResponseDetails> =
    z.discriminatedUnion('outcome', [
        CloverFrontendConfigEditingAppliedResponseDetailsSchema,
        CloverFrontendConfigEditingInvalidConfigurationResponseDetailsSchema,
    ]);
